import type { ReactNode } from 'react';
import { Checkbox } from '@shared/components';
import { cannotAuthoriseReason, confirmationSentence } from '../utils/fundingAuthorisation';
import type { FundingPurpose } from '../types/agreements.types';

export interface FundingAuthorisationProps {
    purpose: FundingPurpose;
    claimed: string;
    excess: string;
    canAuthorise: boolean;
    confirmed: boolean;
    onConfirmedChange: (next: boolean) => void;
    authorisationReason: string | null;
    emailSlot?: ReactNode;
    codeSlot?: ReactNode;
    emailOpen?: boolean;
    notice?: string | null;
    locked?: boolean;
}

const CONFIRM_ID = 'invoice-funding-confirmation';

const ARREARS_EXCESS_NOTE = ' The excess cannot be invoiced until it is authorised.';

function CannotAuthorise({ purpose }: { purpose: FundingPurpose }) {
    return (
        <p className="text-sm text-mp-foreground">
            {cannotAuthoriseReason(purpose)}
            {purpose === 'arrears' && ARREARS_EXCESS_NOTE}
        </p>
    );
}

export function FundingAuthorisation({
    purpose,
    claimed,
    excess,
    canAuthorise,
    confirmed,
    onConfirmedChange,
    authorisationReason,
    emailSlot,
    codeSlot,
    emailOpen = true,
    notice = null,
    locked = false,
}: FundingAuthorisationProps) {
    const sentence = confirmationSentence(purpose, { claimed, excess });

    return (
        <div className="space-y-4">
            <p aria-live="polite" className="sr-only">
                {authorisationReason ?? 'Authorisation confirmed.'}
            </p>

            {canAuthorise ? (
                <Checkbox
                    id={CONFIRM_ID}
                    checked={confirmed}
                    onChange={onConfirmedChange}
                    label={sentence}
                    ariaLabel={sentence}
                    disabled={locked}
                />
            ) : (
                <>
                    {emailSlot === undefined ? (
                        <CannotAuthorise purpose={purpose} />
                    ) : (
                        emailOpen && emailSlot
                    )}
                    {emailSlot !== undefined && (
                        <p role="alert" className="text-xs text-mp-error">
                            {notice}
                        </p>
                    )}
                    {codeSlot}
                </>
            )}
        </div>
    );
}
